import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileX2, Filter, X, Download, RefreshCw, FileText, Eye, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { exportXlsx } from "@/lib/xlsx";

const KIND_LABEL = { finiquito: "Finiquito", traslado: "Anexo traslado" };

export default function Finiquitos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mandantes, setMandantes] = useState([]);
  const [causales, setCausales] = useState([]);
  const [filters, setFilters] = useState({
    kind: "", causal: "", mandante: "", contrato: "", date_from: "", date_to: "", search: "",
  });
  const [applied, setApplied] = useState(filters);
  const [preview, setPreview] = useState(null); // {url, name, worker_id, kind}

  const load = async (f = applied) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/finiquitos", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron cargar los finiquitos");
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/mandantes").then((r) => setMandantes(r.data || [])).catch(() => {});
    api.get("/finiquito/causales").then((r) => setCausales(r.data || [])).catch(() => {});
    load({});
    // eslint-disable-next-line
  }, []);

  const contratos = useMemo(() => {
    const m = mandantes.find((x) => x.name === filters.mandante);
    return m ? (m.contratos || []) : [];
  }, [mandantes, filters.mandante]);

  const apply = () => { setApplied(filters); load(filters); };
  const clear = () => {
    const empty = { kind: "", causal: "", mandante: "", contrato: "", date_from: "", date_to: "", search: "" };
    setFilters(empty); setApplied(empty); load(empty);
  };

  const viewFile = async (r) => {
    if (!r.has_file) { toast.info("Este registro no tiene archivo adjunto"); return; }
    try {
      const res = await api.get(`/workers/${r.worker_id}/finiquito/file`, {
        params: { kind: r.kind }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setPreview({ url, name: r.original_filename || "finiquito.pdf", worker_id: r.worker_id, kind: r.kind });
    } catch (e) { toast.error("No se pudo abrir el archivo"); }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const exportXlsxFile = () => {
    const data = rows.map((r) => ({
      Fecha: r.fecha || (r.at || "").slice(0, 10),
      Tipo: KIND_LABEL[r.kind] || r.kind,
      RUT: r.rut || "",
      Nombre: r.name || "",
      Cargo: r.position || "",
      Mandante: r.mandante || "",
      Contrato: r.contrato || "",
      Causal: r.causal_name || "",
      Nota: r.note || "",
    }));
    exportXlsx(data, `finiquitos_${new Date().toISOString().slice(0, 10)}`, "Finiquitos");
  };

  return (
    <div className="space-y-6" data-testid="finiquitos-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileX2 size={26} className="text-red-600" /> Finiquitos
          </h1>
          <p className="text-slate-500 text-sm mt-1" data-testid="finiquitos-count">
            Historial de finiquitos y anexos de traslado · <b>{rows.length}</b> registro(s).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="finiquitos-refresh" onClick={() => load(applied)} disabled={loading}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
          </button>
          <button data-testid="finiquitos-export" onClick={exportXlsxFile} disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 text-sm font-semibold text-white">
            <Download size={14} /> Exportar Excel
          </button>
        </div>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
          <Filter size={14} /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Buscar">
            <input data-testid="fin-filter-search" value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
              placeholder="Nombre o RUT…"
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
          <Field label="Tipo">
            <select data-testid="fin-filter-kind" value={filters.kind}
              onChange={(e) => setFilters((p) => ({ ...p, kind: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todos</option>
              <option value="finiquito">Finiquito</option>
              <option value="traslado">Anexo de traslado</option>
            </select>
          </Field>
          <Field label="Causal">
            <select data-testid="fin-filter-causal" value={filters.causal}
              onChange={(e) => setFilters((p) => ({ ...p, causal: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todas</option>
              {causales.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Mandante">
            <select data-testid="fin-filter-mandante" value={filters.mandante}
              onChange={(e) => setFilters((p) => ({ ...p, mandante: e.target.value, contrato: "" }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Todos</option>
              {mandantes.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Contrato">
            <select data-testid="fin-filter-contrato" value={filters.contrato}
              onChange={(e) => setFilters((p) => ({ ...p, contrato: e.target.value }))}
              disabled={!filters.mandante}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white disabled:opacity-50">
              <option value="">Todos</option>
              {contratos.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Desde">
            <input data-testid="fin-filter-from" type="date" value={filters.date_from}
              onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
          <Field label="Hasta">
            <input data-testid="fin-filter-to" type="date" value={filters.date_to}
              onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button data-testid="fin-apply" onClick={apply}
            className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">
            Aplicar filtros
          </button>
          <button data-testid="fin-clear" onClick={clear}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700">
            <X size={14} /> Limpiar
          </button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="finiquitos-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2 whitespace-nowrap">Fecha</th>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2 whitespace-nowrap">RUT</th>
                <th className="px-4 py-2">Trabajador</th>
                <th className="px-4 py-2">Cargo</th>
                <th className="px-4 py-2">Mandante</th>
                <th className="px-4 py-2">Contrato</th>
                <th className="px-4 py-2">Causal</th>
                <th className="px-4 py-2 w-20 text-right">Archivo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center text-slate-400 italic p-10">Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-slate-400 italic p-10">Sin finiquitos para los filtros aplicados.</td></tr>
              ) : rows.map((r) => (
                <tr key={`${r.worker_id}-${r.kind}`} data-testid={`finiquito-row-${r.worker_id}-${r.kind}`}
                  className="border-t border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{(r.fecha || (r.at || "").slice(0, 10)) || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      r.kind === "finiquito" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                    }`}>{KIND_LABEL[r.kind] || r.kind}</span>
                  </td>
                  <td className="px-4 py-2 text-xs font-mono text-slate-700 whitespace-nowrap">{r.rut || "—"}</td>
                  <td className="px-4 py-2 font-semibold text-slate-800">{r.name}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{r.position || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 inline-flex items-center gap-1">
                    {r.mandante && <Building2 size={11} className="text-slate-400" />} {r.mandante || "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{r.contrato || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-xs">
                    {r.causal_name || "—"}
                    {r.note && <p className="text-[10px] text-slate-400 italic mt-0.5 truncate">{r.note}</p>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {r.has_file ? (
                      <button data-testid={`finiquito-view-${r.worker_id}-${r.kind}`}
                        onClick={() => viewFile(r)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md">
                        <Eye size={11} /> Ver
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">Sin archivo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="bg-white max-w-5xl p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-slate-100">
            <DialogTitle className="font-display text-base flex items-center gap-2">
              <FileText size={16} className="text-red-600" /> {preview?.name}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <iframe title="preview-finiquito" src={preview.url} className="w-full h-[75vh]"
              data-testid="finiquito-preview-iframe" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}
